// src/pages/public/Contact/ContactPage.jsx
import React, { useEffect, useRef, useState } from 'react'
import { motion as Motion, useInView } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch.js'
import { fetchPageBySlug } from '../../../redux/slices/cmsSlice.js'
import SEO from '../../../components/seo/SEO.jsx'
import './ContactPage.scss'
import Newsletter from '../../../components/common/Common/NewsLetter.jsx'
import { FiMapPin } from "react-icons/fi";
import { BsTelephone } from "react-icons/bs";
import BuildSolution from '../../../components/common/Common/BuildSolution/BuildSolution.jsx'

import { CiMail } from "react-icons/ci";
import { formService } from '../../../services/formServices'; // Adjust path as needed

export default function ContactPage() {
  const dispatch = useAppDispatch()
  const { currentPage: contactPage, loading: pageLoading } = useAppSelector((state) => state.cms)
  const formRef = useRef();

  // State for form handling
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    dispatch(fetchPageBySlug('contact'))
  }, [dispatch])

  const getSectionData = (sectionKey) => {
    return contactPage?.sections?.find(section => section.key === sectionKey)?.data
  }

  const heroSection = getSectionData('hero')
  const globalOfficesSection = getSectionData('globalOffices')

  // For global offices animation
  const officesSectionRef = useRef(null)
  const isOfficesInView = useInView(officesSectionRef, { amount: 0.3, once: false })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: 'beforeChildren',
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  }

  // Process global offices data according to the new structure
  const processGlobalOfficesData = () => {
    if (!globalOfficesSection?.cards) return { offices: [], services: [], social: null, mapUrl: null }

    const cards = globalOfficesSection.cards
    const offices = [cards[0]]
    let services = cards[2]
    let social = cards[3]
    let mapUrl = cards[4]?.mapUrl

    return { offices, services, social, mapUrl }
  }

  const { offices, services, social, mapUrl } = processGlobalOfficesData()

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    const nameRegex = /^[a-zA-Z ]{2,30}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!nameRegex.test(formData.name)) {
      errors.name = "Name must be between 2 and 30 characters and only contain letters and spaces";
    }

    if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email address";
    }

    if (!formData.interest) {
      errors.interest = "Please select your interest";
    }

    if (!formData.message.trim()) {
      errors.message = "Please enter your message";
    }

    return errors;
  };

  const submitForm = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API
      const submissionData = {
        name: formData.name,
        email: formData.email,
        interest: formData.interest,
        message: formData.message,
        sourcePage: window.location.pathname,
        formType: 'contact_page'
      };

      // Call API
      await formService.submitContactForm(submissionData);

      // Success - show alert or notification
      alert("Thank you for contacting us! We'll get back to you soon.");

      // Reset form
      setFormData({
        name: '',
        email: '',
        interest: '',
        message: '',
      });
      setFormErrors({});

      // Reset form ref if needed
      if (formRef.current) {
        formRef.current.reset();
      }

    } catch (error) {
      console.error('Contact form submission error:', error);
      alert(error.response?.data?.message || "Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pageLoading && !contactPage) {
    return (
      <>
        <SEO title="Contact Us" />
        <div className="space-y-20">
          {/* Hero Skeleton */}
          <section className="bg-gray-50">
            <div className="container-custom section-padding">
              <div className="max-w-3xl mx-auto text-center animate-pulse">
                <div className="h-12 bg-black-200 rounded w-1/2 mx-auto mb-6"></div>
                <div className="h-6 bg-black-200 rounded w-3/4 mx-auto mb-8"></div>
              </div>
            </div>
          </section>

          {/* Contact Form Skeleton */}
          <section className="bg-white">
            <div className="container-custom section-padding">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="space-y-4 animate-pulse">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-12 bg-black-200 rounded"></div>
                  ))}
                </div>
                <div className="space-y-4 animate-pulse">
                  <div className="h-48 bg-black-200 rounded"></div>
                  <div className="h-12 bg-black-200 rounded"></div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title={contactPage?.seoTitle || 'Contact Us'}
        description={contactPage?.seoDescription}
      />

      <div className="space-y-20">
        {/* HERO SECTION – v1 UI, v2 data + integrated form */}
        <section className="contact-hero-section">
          <div className="background-image-container" />
          <div className="background-overlay" />

          <div className="contact-content-container">
            {/* Left: Heading + subtitle */}
            <div className="contact-text-col">
              <Motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="contact-heading"
              >
                {(heroSection?.heading || '').toUpperCase()}
              </Motion.h1>

              <Motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="contact-subheading"
              >
                {heroSection?.subheading ||
                  ""}
              </Motion.p>
            </div>

            {/* Right: Form box – integrated form from HeroSection */}
            <div className="contact-form-col">
              <Motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="contact-form-box"
              >
                <h3 className="form-heading">
                  {/* {heroSection?.formTitle || 'Get in Touch With LogiQlink'} */}
                  Get in Touch With LogiQlink
                </h3>

                {/* Integrated Form from HeroSection */}
                <form ref={formRef} className="contact-form" onSubmit={submitForm}>
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder='Enter your full name'
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className={formErrors.name ? 'error' : ''}
                    />
                    {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder='Email address'
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className={formErrors.email ? 'error' : ''}
                    />
                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <select
                      placeholder='Select Interest'
                      id="interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className={formErrors.interest ? 'error' : ''}
                    >
                      <option value="">Select Interest</option>
                      <option value="general">General Inquiry</option>
                      <option value="project">Project Discussion</option>
                      <option value="support">Technical Support</option>
                      <option value="careers">Careers</option>
                      <option value="consultation">Consultation</option>
                      <option value="partnership">Partnership</option>
                      <option value="other">Other</option>
                    </select>
                    {formErrors.interest && <span className="error-message">{formErrors.interest}</span>}
                  </div>
                  <div className="form-group">
                    <textarea
                      id="message"
                      placeholder='Write your message...'
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="5"
                      required
                      disabled={isSubmitting}
                      className={formErrors.message ? 'error' : ''}
                    ></textarea>
                    {formErrors.message && <span className="error-message">{formErrors.message}</span>}
                  </div>
                  <Motion.button
                    type="submit"
                    whileHover={!isSubmitting ? { scale: 1.05 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                    disabled={isSubmitting}
                    className="submit-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Motion.button>
                </form>
              </Motion.div>
            </div>
          </div>
        </section>

        {/* GLOBAL OFFICES – Updated to match NewsLetterList UI with new data structure */}
        <section className="newsletter-list" ref={officesSectionRef}>
          {globalOfficesSection && (
            <Motion.div
              className="container"
              initial="hidden"
              animate={isOfficesInView ? 'visible' : 'hidden'}
              variants={itemVariants}
            >
              <Motion.div
                className="section-header"
                initial="hidden"
                animate={isOfficesInView ? 'visible' : 'hidden'}
                variants={containerVariants}
              >
                <Motion.h2 variants={itemVariants}>
                  {(globalOfficesSection.heading || 'GLOBAL OFFICES').toUpperCase()}
                </Motion.h2>
                {globalOfficesSection.subheading && (
                  <Motion.p className="subheading" variants={itemVariants}>
                    {globalOfficesSection.subheading}
                  </Motion.p>
                )}
              </Motion.div>

              <div
                className="offices-grid"
                style={{ maxWidth: '1000px', margin: '0 auto' }}
              >
                {/* First Row - Office Locations */}
                <Motion.div
                  className="row"
                  initial="hidden"
                  animate={isOfficesInView ? 'visible' : 'hidden'}
                  variants={containerVariants}
                >
                  {offices.map((office, index) => (
                    <Motion.div
                      key={index}
                      className="col"
                      variants={itemVariants}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="office-card">
                        <div className="card-header">
                          <FiMapPin className="flag-icon" />
                          <h3>{office.heading}</h3>
                        </div>
                        <div className="card-body">
                          <p className="address">{office.subheading}</p>
                          {office.list && office.list.length > 0 && (
                            <>
                              {office.list.map((item, i) => {
                                // Check if it's a phone number or email
                                const isPhone = item.includes('+')
                                const isEmail = item.includes('@')

                                return (
                                  <div key={i} className="contact-line">
                                    {isPhone ? (
                                      <BsTelephone fontSize={16} color='#aaa' />
                                    ) : isEmail ? (
                                      <CiMail fontSize={18} color='#aaa' />
                                    ) : null}
                                    <p>{item}</p>
                                  </div>
                                )
                              })}
                            </>
                          )}
                        </div>
                      </div>
                    </Motion.div>
                  ))}
                </Motion.div>

                {/* Second Row - Services and Social */}
                {(services || social) && (
                  <Motion.div
                    className="row"
                    initial="hidden"
                    animate={isOfficesInView ? 'visible' : 'hidden'}
                    variants={containerVariants}
                    transition={{ delay: 0.2 }}
                  >
                    {/* Services Card */}
                    {services && (
                      <Motion.div
                        className="col"
                        variants={itemVariants}
                      >
                        <div className="services-card">
                          <div className="service-item">
                            <h4>{services.heading}</h4>
                            {services.list && services.list.length > 0 && (
                              <>
                                {services.list.map((item, i) => (
                                  <p key={i}>{item}</p>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </Motion.div>
                    )}

                    {/* Social Card */}
                    {social && (
                      <Motion.div
                        className="col"
                        variants={itemVariants}
                        transition={{ delay: 0.3 }}
                      >
                        <div className="social-card">
                          <h3 className="">{social.heading || "Follow Us"}</h3>
                          <div className="social-icons">
                            {social.list && social.list.length > 0 && social.list.map((socialItem, index) => (
                              socialItem.iconUrl ? (
                                <a
                                  key={index}
                                  href={socialItem.iconLink || "#"}
                                  aria-label={`Social icon ${index + 1}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={socialItem.iconUrl}
                                    alt={`Social icon ${index + 1}`}
                                    className="social-icon-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </a>
                              ) : null
                            ))}
                          </div>
                        </div>
                      </Motion.div>
                    )}
                  </Motion.div>
                )}

                {/* Third Row - Map */}
                <Motion.div
                  className="row map-row"
                  initial="hidden"
                  animate={isOfficesInView ? 'visible' : 'hidden'}
                  variants={itemVariants}
                  transition={{ delay: 0.4 }}
                >
                  <div className="col-full">
                    <div className="map-container">
                      <iframe
                        src={mapUrl || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.025252641457!2d75.8873856!3d22.7644441!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39630382136ddb17%3A0x7c14e5590cdc3862!2sLogiQlink%20Technologies!5e0!3m2!1sen!2sin!4v1756209962800!5m2!1sen!2sin"}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="LogiQlink Technologies Location"
                      ></iframe>
                    </div>
                  </div>
                </Motion.div>
              </div>
            </Motion.div>
          )}
        </section>
        <BuildSolution />
        <Newsletter />
      </div>
    </>
  )
}